import { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext();

const defaultTheme = {
  mode: "light",
  lightAccent: "#9a6a32",
  darkAccent: "#d6b16f",
  accent: "#9a6a32",
};

const hexToRgb = (hex) => {
  const clean = hex.replace("#", "");
  const bigint = parseInt(clean, 16);

  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

const mix = (hex, amount, target = "#ffffff") => {
  const c1 = hexToRgb(hex);
  const c2 = hexToRgb(target);

  const r = Math.round(c1.r * (1 - amount) + c2.r * amount);
  const g = Math.round(c1.g * (1 - amount) + c2.g * amount);
  const b = Math.round(c1.b * (1 - amount) + c2.b * amount);

  return `rgb(${r}, ${g}, ${b})`;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("adminTheme");

    if (!saved) return defaultTheme;

    const parsed = JSON.parse(saved);

    return {
      ...defaultTheme,
      ...parsed,
      lightAccent: parsed.lightAccent || "#9a6a32",
      darkAccent: parsed.darkAccent || "#d6b16f",
      accent:
        parsed.mode === "dark"
          ? parsed.darkAccent || "#d6b16f"
          : parsed.lightAccent || "#9a6a32",
    };
  });

  useEffect(() => {
    const root = document.documentElement;

    const accentDark = mix(theme.accent, 0.68, "#000000");
    const accentDeep = mix(theme.accent, 0.82, "#000000");
    const accentLight = mix(theme.accent, 0.42, "#ffffff");
    const accentSoft =
      theme.mode === "dark"
        ? mix(theme.accent, 0.84, "#171b24")
        : mix(theme.accent, 0.88, "#ffffff");

    root.setAttribute("data-theme", theme.mode);

    root.style.setProperty("--accent", theme.accent);
    root.style.setProperty("--accent-dark", accentDark);
    root.style.setProperty("--accent-deep", accentDeep);
    root.style.setProperty("--accent-light", accentLight);
    root.style.setProperty("--accent-soft", accentSoft);

    root.style.setProperty("--button-bg", theme.accent);

    root.style.setProperty("--sidebar-from", accentDeep);
    root.style.setProperty("--sidebar-mid", accentDark);
    root.style.setProperty("--sidebar-to", theme.mode === "dark" ? "#050608" : "#080604");

    root.style.setProperty(
      "--navbar-bg",
      theme.mode === "dark" ? "#171b24" : "#ffffff"
    );

    localStorage.setItem("adminTheme", JSON.stringify(theme));
  }, [theme]);

  const toggleMode = () => {
    setTheme((prev) => {
      const newMode = prev.mode === "light" ? "dark" : "light";

      return {
        ...prev,
        mode: newMode,
        accent: newMode === "light" ? prev.lightAccent : prev.darkAccent,
      };
    });
  };

  const changeAccent = (colour) => {
    setTheme((prev) => ({
      ...prev,
      accent: colour,
      ...(prev.mode === "light"
        ? { lightAccent: colour }
        : { darkAccent: colour }),
    }));
  };

  const resetTheme = () => {
    setTheme(defaultTheme);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleMode,
        changeAccent,
        resetTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
